// src/match/infrastructure/ui-services/matchUiService.ts

import type { MatchStatusDTO } from "~/src/match/infrastructure/DTO/MatchDTO";
import type { MatchStatusSimplified } from "~/src/match/domain/models/Match";
import type { Event } from "~/src/match/domain/models/Event";
import type { ParameterCode } from "~/src/stats/domain/models/Parameter";
import matchService, {
  $MATCH_DICE_PARAMS,
  $MATCH_TIMELINE_EVENT_PARAMS,
} from "~/src/match/domain/services/matchService";
import { useWebContext } from "~/app/composables/webContext";

interface MatchUiService {
  transformStatus: (status: MatchStatusDTO | null | undefined) => MatchStatusSimplified | null;
  formatEventMinute: (event: Event, seasonClusterId: number) => string;
  getMatchingCode: (
    parameterAppString: string,
    seasonClusterId?: number,
    isStar?: boolean
  ) => ParameterCode | string | undefined;
  getMatchingCodeImage: (
    parameterAppString: string,
    seasonClusterId?: number,
    isStar?: boolean
  ) => ParameterCode | string | undefined;
}

const service = (): MatchUiService => ({
  transformStatus,
  formatEventMinute,
  getMatchingCode,
  getMatchingCodeImage,
});

export default service;

function formatEventMinute(event: Event, seasonClusterId: number): string {
  if (!event) return "";
  const halfDuration = 20;
  const minute = Math.ceil(event.matchSecond / 60);

  if (seasonClusterId >= 3) {
    return `${minute}'`;
  }

  const endHalfMinute = halfDuration * event.half;
  const extraMinute = minute - endHalfMinute;
  if (minute < endHalfMinute || extraMinute <= 0) {
    return `${minute}'`;
  } else {
    return `${endHalfMinute}+${extraMinute}'`;
  }
}

function transformStatus(status: MatchStatusDTO | null | undefined): MatchStatusSimplified | null {
  if (!status) return null;

  switch (status) {
    case "notStarted":
      return "scheduled";

    case "inPlay1H":
    case "inPlay2H":
    case "inPlay1EH":
    case "inPlay2EH":
    case "inPlayP":
    case "halfTime":
    case "breakTime":
      return "live";

    case "ended":
    case "endedAfterExtraTime":
    case "endedAfterPenalties":
      return "ended";

    case "postponed":
      return "rescheduled";

    case "cancelled":
      return "cancelled";

    case "abandoned":
      return "abandoned";

    case null:
      return "ended";

    default:
      return status;
  }
}

function getMatchingCode(
  parameterAppString: string,
  seasonClusterId?: number,
  isStar?: boolean
): ParameterCode | string | undefined {
  const { findGoalEvent, findMissedPenalty } = matchService();
  let eventCode;
  const parameters = parameterAppString.split(",");

  if (parameters.some(e => e === "GOL-ALL")) {
    eventCode = findGoalEvent(parameterAppString, seasonClusterId, false, isStar);
  } else if (parameters.some(e => e === "RIG-F") || parameters.some(e => e === "RIG-F-PRES")) {
    eventCode = findMissedPenalty(parameterAppString);
  } else if (parameters.some(e => e === "SHO-F") || parameters.some(e => e === "SHO-F-PRES")) {
    eventCode = "SHO-F";
  } else if (parameters.some(e => e === "CARD-RPE-END")) {
    return "CARD-RPE-ACT-F";
  } else {
    eventCode = [...$MATCH_TIMELINE_EVENT_PARAMS, ...$MATCH_DICE_PARAMS].find(code =>
      parameters.some(e => e === code)
    );
  }

  return eventCode;
}

function getMatchingCodeImage(
  parameterAppString: string,
  seasonClusterId?: number,
  isStar?: boolean
): ParameterCode | string | undefined {
  const { findGoalEvent, findMissedPenalty } = matchService();
  const { webContext } = useWebContext();
  let eventCode;

  const parameters = parameterAppString.split(",");
  if (parameters.some(e => e === "GOL-ALL")) {
    eventCode = findGoalEvent(parameterAppString, seasonClusterId, false, isStar);
  } else if (parameters.some(e => e === "RIG-F") || parameters.some(e => e === "RIG-F-PRES")) {
    eventCode = findMissedPenalty(parameterAppString);
  } else if (parameters.some(e => e === "SHO-F") || parameters.some(e => e === "SHO-F-PRES")) {
    eventCode = "SHO-F";
  } else if (parameters.some(e => e === "CARD-RPE-END")) {
    return "CARD-RPE-ACT-F";
  } else if (parameters.some(e => e === "DICE-1GK-ACT")) {
    return webContext === "queens" ? "DICE-1GK-ACT-QL" : "DICE-1GK-ACT-KL";
  } else {
    eventCode = [...$MATCH_TIMELINE_EVENT_PARAMS, ...$MATCH_DICE_PARAMS].find(code =>
      parameters.some(e => e === code)
    );
  }

  return eventCode;
}
